/* eslint-disable camelcase */
import NiceModal from '@ebay/nice-modal-react';
import dayjs from 'dayjs';

import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { sallaAxios } from 'services/axios';
import { SALES_CHANNELS } from 'services/consts';

// Ensure this is imported

import AppLogger from 'utils/AppLogger';
import { store } from 'utils/configureStore';
import PATHS, { mergePaths } from 'utils/paths';

import { fetchSallaStores, setLastError, setSyncInfo, updateSallaUpdateInfo } from './ConnectedStores/Salla.slice';
import SallaConnectModal from './Stores/Salla/SallaConnectModal';

/**
 * Custom hook for syncing orders from Salla to OTO backend
 * @returns {Object} Functions and state for Salla sync operation
 */

let intervalRef = {};
let updateIntervalRef = {};

const useSallaSync = () => {
	const [isLoading, setIsLoading] = useState(false);
	const { showError } = useAlert();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const salla = useSelector((state) => state.salla);
	const [isSyncingSallaOrders, setIsSyncingSallaOrders] = useState(false);
	const axiosPrivate = useAxiosPrivate();
	// Using Redux state instead of local storage
	const sallaUpdateInfo = useSelector((state) => state.salla.sallaUpdateInfo);
	const [isSyncingStatus, setIsSyncingStatus] = useState(false); // New state for this specific sync

	const fetchOrdersFromSalla = async ({ merchant, salesChannelId, orderID, nextPage }) => {
		const storeSyncInfo = salla.syncInfo?.find((item) => item.salesChannelId === salesChannelId);
		const fromDate = dayjs(storeSyncInfo?.lastSyncDate ?? dayjs().subtract(7, 'day').toISOString()).format(
			'YYYY-MM-DD HH:mm:ss'
		);

		let params = {
			from_date: fromDate,
			per_page: 1000,
			expanded: 'light'
		};
		if (orderID) {
			params = {
				reference_id: orderID,
				expanded: 'light'
			};
		}

		try {
			const nextUrlParam = nextPage ? new URL(nextPage)?.search : '';
			const sallaResponse = await sallaAxios.get(`/orders${nextUrlParam}`, {
				params,
				headers: {
					Authorization: `Bearer ${merchant?.token}`,
					'Content-Type': 'application/json'
				}
			});

			// Log successful request
			logEcommerceRequest({
				isFailure: false,
				url: 'https://api.salla.dev/admin/v2/orders',
				request: JSON.stringify({ params, merchantId: merchant?.id }),
				response: JSON.stringify(sallaResponse?.data.map((item) => item?.reference_id))?.slice?.(0, 254),
				action: 'fetchOrdersFromSalla',
				orderId: orderID || 'bulk_fetch'
			});

			return sallaResponse;
		} catch (error) {
			// Log failed request
			logEcommerceRequest({
				isFailure: true,
				url: 'https://api.salla.dev/admin/v2/orders',
				request: JSON.stringify({ params, merchantId: merchant?.id }),
				response: JSON.stringify(error.response?.data || error.message),
				action: 'fetchOrdersFromSalla',
				orderId: orderID || 'bulk_fetch'
			});

			throw error;
		}
	};

	const syncOrders = async ({ merchantId, salesChannelId, updatedStoreList, showInitialError }) => {
		try {
			setIsLoading(true);
			let sallaStoreList = updatedStoreList;

			if (!updatedStoreList) {
				sallaStoreList = await dispatch(fetchSallaStores({ forceSync: false })).unwrap();
			}

			const merchant = sallaStoreList?.find((item) => item.id === Number(merchantId));
			if (!merchant) {
				// if localstorage for sallaStores is empty, clear the interval
				const error = new Error('Merchant not found');
				error.stack = 'syncOrders: Merchant not found';
				clearInterval(intervalRef[salesChannelId]);
				clearInterval(updateIntervalRef[salesChannelId]);
				throw error;
			}
			const ordersData = {
				data: [],
				pagination: null
			};
			const orders = await fetchOrdersFromSalla({ merchant, salesChannelId });
			ordersData.data.push(...orders.data);
			ordersData.pagination = orders.pagination;
			const totalOrders = orders?.pagination?.total || orders?.data?.length;
			if (ordersData.pagination?.links?.next) {
				// if there are more pages, fetch them
				let nextPage = orders.pagination.links.next;
				while (nextPage) {
					const nextOrders = await fetchOrdersFromSalla({ merchant, salesChannelId, nextPage });
					ordersData.data.push(...nextOrders.data);
					ordersData.pagination = nextOrders.pagination;
					nextPage = nextOrders.pagination?.links?.next;
					if (
						!nextOrders?.pagination?.links?.next ||
						nextOrders?.pagination?.currentPage === nextOrders?.pagination?.totalPages
					) {
						break;
					}
				}
			}
			const webhookEndpoint = '/webhook/salesChannel/aHh2c2NGRS9qbWpzbjhOeloxRzl1bWFZZk5oMUxBVWQ=';
			const requestPayload = {
				data: JSON.stringify(ordersData.data),
				merchantId: merchant.id,
				companyId: store.getState().user?.companyId ?? 0,
				platform: 'desktop'
			};

			const otoResponse = await axiosPrivate.post(webhookEndpoint, requestPayload);

			if (otoResponse.status !== 200) {
				throw new Error('Failed to sync orders');
			}

			// Log successful synchronization
			logEcommerceRequest({
				isFailure: false,
				url: webhookEndpoint,
				request: JSON.stringify({ ...requestPayload, orderCount: totalOrders }),
				response: JSON.stringify(otoResponse?.data),
				action: 'syncOrders',
				orderId: `batch_${salesChannelId}`
			});

			dispatch(
				setSyncInfo({
					salesChannelId,
					merchantId,
					orderCount: totalOrders,
					lastSyncDate: new Date().toString()
				})
			);

			return otoResponse;
		} catch (err) {
			// if salla token is expired, clear the interval
			if (err?.response?.status === 401) {
				clearInterval(intervalRef[salesChannelId]);
				clearInterval(updateIntervalRef[salesChannelId]);
				if (showInitialError) {
					showError({
						message: 'errorMsg.sallaTokenExpired',
						link: 'errorMsg.goToConnectedStore',
						linkAction: () => {
							navigate(mergePaths(PATHS.SALES_CHANNELS, PATHS.CONNECTED_STORES));
						}
					});
				}
			}

			logEcommerceRequest({
				isFailure: true,
				url: 'syncOrders',
				request: JSON.stringify({ merchantId, salesChannelId }),
				response: JSON.stringify({ error: err.message, stack: err.stack }),
				action: 'syncOrders'
			});
			dispatch(setLastError({ salesChannelId, lastErrorDate: new Date().toString() }));
			AppLogger.error(err);
			return {
				success: false,
				error: err.message
			};
		} finally {
			setIsLoading(false);
		}
	};

	const updateSallaOrderList = async (orderUpdates, merchant, merchantId) => {
		for (const orderUpdate of orderUpdates) {
			const { orderId, requestBody, entityId, status: otoStatus } = orderUpdate;
			let sallaResponseData = null;

			try {
				if (!localStorage.getItem('REFRESH_TOKEN') && !localStorage.getItem('ACCESS_TOKEN')) {
					break;
				}
				const statusEndpoint = `/orders/${entityId}/status`;
				const sallaResponse = await sallaAxios.post(statusEndpoint, requestBody, {
					headers: {
						Authorization: `Bearer ${merchant.token}`
					}
				});

				// Log update status to Salla request
				logEcommerceRequest({
					isFailure: !sallaResponse.success,
					url: `https://api.salla.dev/admin/v2/orders/${entityId}/status`,
					request: JSON.stringify(requestBody),
					response: JSON.stringify(sallaResponse?.data || {}),
					action: 'updateSallaStatus',
					ecommerceStatus: otoStatus,
					orderId
				});

				if (sallaResponse.success) {
					dispatch(
						updateSallaUpdateInfo({
							merchantId,
							lastUpdateDate: new Date()
						})
					);
				} else {
					throw new Error(`Salla API returned status ${sallaResponse.status}`);
				}
			} catch (sallaError) {
				sallaResponseData = sallaError.response?.data ?? { error: sallaError.message };

				// Log error in updating Salla status
				logEcommerceRequest({
					isFailure: true,
					url: `https://api.salla.dev/admin/v2/orders/${entityId}/status`,
					request: JSON.stringify(requestBody),
					response: JSON.stringify(sallaResponseData),
					action: 'updateSallaStatus',
					ecommerceStatus: otoStatus,
					orderId
				});
			} finally {
				// avoid rate limiting by adding a random delay
				// eslint-disable-next-line sonarjs/pseudo-random
				await new Promise((resolve) => setTimeout(resolve, 1000 + Math.random() * 300));
			}
		}
	};

	const syncSallaOrderStatus = useCallback(async (merchantId) => {
		setIsSyncingStatus(true);
		const sallaStoreList = await dispatch(fetchSallaStores({ forceSync: false })).unwrap();
		const merchant = sallaStoreList?.find((item) => item.id === Number(merchantId));

		if (!merchant?.token) {
			setIsSyncingStatus(false);
			return;
		}
		const storeUpdateInfo = sallaUpdateInfo?.find((item) => item.merchantId === merchantId);
		const filterDate = dayjs(storeUpdateInfo?.lastUpdateDate ?? dayjs().subtract(7, 'day').toISOString()).format(
			'YYYY-MM-DD HH:mm:ss'
		);
		try {
			const updatesRequestPayload = {
				merchantId: String(merchantId),
				filterDate
			};

			const updatesResponse = await axiosPrivate.post(
				SALES_CHANNELS.GET_SALLA_AUTOMATION_UPDATES,
				updatesRequestPayload
			);

			if (updatesResponse.status !== 200 || !updatesResponse?.data?.orderUpdates) {
				throw new Error('Failed to fetch salla automation updates or invalid data received.');
			}

			const orderUpdates = updatesResponse?.data.orderUpdates;
			// Log fetching order updates
			logEcommerceRequest({
				isFailure: updatesResponse.status !== 200,
				url: SALES_CHANNELS.GET_SALLA_AUTOMATION_UPDATES,
				request: JSON.stringify(updatesRequestPayload),
				response: JSON.stringify(updatesResponse?.data),
				action: 'getOrderUpdates'
			});
			updateSallaOrderList(orderUpdates, merchant, merchantId);
		} catch (error) {
			// Log general error in status sync process
			logEcommerceRequest({
				isFailure: true,
				url: 'syncSallaOrderStatus',
				request: JSON.stringify({ merchantId, filterDate }),
				response: JSON.stringify({ error: error.message, stack: error.stack }),
				action: 'updateSallaStatus'
			});

			AppLogger.error(`Error during Salla status sync for merchant ${merchantId}:`, error);
		} finally {
			setIsSyncingStatus(false);
		}
	}, []);

	const fetchConnectedSallaChannels = useCallback(async () => {
		try {
			const response = await axiosPrivate.post(SALES_CHANNELS.GET_CONNECTED_SALLA_CHANNELS, {});
			if (response.status === 200) {
				return response?.data;
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, []);

	const startAutomation = async (updatedStoreList, showInitialError) => {
		Object.values(intervalRef).forEach((interval) => clearInterval(interval));
		Object.values(updateIntervalRef).forEach((interval) => clearInterval(interval));
		updateIntervalRef = {};
		intervalRef = {};
		const channels = await fetchConnectedSallaChannels();

		if (channels?.length > 0) {
			channels.forEach((channel) => {
				if (!channel.active) return;
				const syncIntervalMinutes = channel.syncInterval || 5;
				const intervalMs = syncIntervalMinutes * 60 * 1000;

				syncOrders({
					merchantId: channel.merchantId,
					salesChannelId: channel.ID,
					updatedStoreList,
					showInitialError
				});
				syncSallaOrderStatus(channel.merchantId);

				const intervalId = setInterval(() => {
					syncOrders({
						merchantId: channel.merchantId,
						salesChannelId: channel.ID,
						updatedStoreList,
						showInitialError
					});
				}, intervalMs);
				intervalRef[channel.ID] = intervalId;

				const updateIntervalMs = 5 * 60 * 1000;
				const updateIntervalId = setInterval(() => {
					syncSallaOrderStatus(channel.merchantId);
				}, updateIntervalMs);
				updateIntervalRef[channel.ID] = updateIntervalId;
			});
			setIsSyncingSallaOrders(true);
		}
	};

	const stopAutomation = () => {
		Object.values(intervalRef).forEach((interval) => clearInterval(interval));
		Object.values(updateIntervalRef).forEach((interval) => clearInterval(interval));
		intervalRef = {};
		updateIntervalRef = {};
		setIsSyncingSallaOrders(false);
	};

	const restartAutomation = (updatedStoreList) => {
		stopAutomation();
		startAutomation(updatedStoreList, false);
	};

	const fetchOrdersWithIDs = useCallback(
		async (orderIDs, salesChannelId, merchantId) => {
			try {
				const sallaStoreList = await dispatch(fetchSallaStores({ forceSync: false })).unwrap();
				const merchant = sallaStoreList?.find((item) => item.id === Number(merchantId));
				if (!merchant) {
					return null;
				}

				let successCount = 0,
					errorCount = 0;
				for (const orderID of orderIDs) {
					const order = await fetchOrdersFromSalla({ merchant, salesChannelId, orderID });
					if (order?.data) {
						const result = await axiosPrivate.post(
							'/webhook/salesChannel/aHh2c2NGRS9qbWpzbjhOeloxRzl1bWFZZk5oMUxBVWQ=',
							{
								data: JSON.stringify(order?.data),
								merchantId: merchant.id,
								companyId: store.getState().user?.companyId ?? 0,
								platform: 'desktop'
							}
						);
						if (result.status === 200) {
							successCount += 1;
						} else {
							errorCount += 1;
							AppLogger.error(result);
						}
					} else {
						AppLogger.error('fetchOrdersWithIDs order:', order);
						errorCount += 1;
					}
				}

				return {
					successCount,
					errorCount
				};
			} catch (err) {
				AppLogger.error(err);
				throw err;
			}
		},
		[axiosPrivate, fetchOrdersFromSalla]
	);

	const logEcommerceRequest = async (body) => {
		try {
			axiosPrivate.post(SALES_CHANNELS.LOG_ECOMMERCE, body);
		} catch (error) {
			AppLogger.error('Error logging ecommerce request:', error);
		}
	};

	const reConnectSalla = async () => {
		NiceModal.show(SallaConnectModal).then((stores) => {
			restartAutomation(stores);
		});
	};

	return {
		isLoading,
		isSyncingSallaOrders,
		isSyncingStatus,
		syncOrders,
		fetchOrdersFromSalla,
		startAutomation,
		stopAutomation,
		restartAutomation,
		fetchOrdersWithIDs,
		syncSallaOrderStatus,
		fetchConnectedSallaChannels,
		logEcommerceRequest,
		reConnectSalla,
		updateSallaOrderList
	};
};

export default useSallaSync;
